import React, { useState, useEffect, useMemo, useContext } from 'react';

import { List, Tag } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';
import { sliceData, checkValidPage } from '@libs/NwUtils';

import { ListContext } from '@contexts/ListContext';
import { useStateValue } from '@contexts/StateProvider';
import { useUserPermission } from '@contexts/UserContext';

import { forceReloadContact } from '@hooks/API/Contact/UseContact';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import NoteAddEditDrawer from '@components/ContactDetailsNotes/NoteAddEditDrawer';
import CreatedBy from '@components/CreatedBy/CreatedBy';
import EmptyState from '@components/Gui/EmptyState';
import FlexContainer from '@components/Gui/FlexContainer';
import { NwAddButton } from '@components/Gui/NwButton';
import { NwCardContent, NwCard } from '@components/Gui/NwCard/NwCard';
import { NwCardTabsContent } from '@components/Gui/NwCard/NwCardTabs';
import NwDrawer from '@components/Gui/NwDrawer';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwEllipsis from '@components/Gui/NwEllipsis';
import NwIcon from '@components/Gui/NwIcon';
import Visibilities from '@components/Gui/Visibilities';
import ObjectLogHistories from '@components/ObjectLogHistories/ObjectLogHistories';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

// const DetailsCardContent = styled.div`
//     display: flex;
//     flex: 1;
//     flex-direction: column;
//     justify-content: flex-start;
//     .tabs-content {
//         justify-content: ${props => !props.viewNotes && 'center'}
//     }
// `;

const LockContainer = styled(FlexContainer)`
    height: 100%;
    padding: ${(props) => props.$drawerMode && '32px 0'};
    svg {
        font-size: ${(props) => (props.$drawerMode ? '80px' : '120px')};
    }
`;

const Wrapper = styled.div`
    .ant-list-item {
        align-items: flex-start;
    }
    .note-description-container {
        .tag-container {
            min-width: 90px;
            padding-bottom: 0.5rem;

            .ant-tag {
                color: white;
                border-color: ${(props) => props.theme.PrimaryDarkLight};
                background-color: ${(props) => props.theme.PrimaryDarkLight};
            }
        }

        p.note-text {
            color: ${(props) => props.theme.TextColor};
            font-size: ${(props) => props.theme.FontSizeBase};
        }

        .created-by {
            font-size: ${(props) => props.theme.FontSizeSm};
            color: ${(props) => props.theme.TextColorMuted};
            text-transform: uppercase;
        }
    }
`;

const ContactDetailsNotes = ({
    currentContact,
    readOnly,
    isInCardTab,
    drawerMode,
    createNotes,
    afterNotesCreation,
    noPagination,
    noEllipsis,
    type,
}) => {
    const [activePage, setActivePage] = useState(1);
    const itemsPerPage = 3;
    const [editingMode, setEditingMode] = useState(false);
    const [itemToModify, setItemToModify] = useState(null);
    const [createMode, setCreateMode] = useState(createNotes);
    //const { data: currentContact } = useContact(contactID)
    const [{ areaContext }] = useStateValue();
    const { cachedList } = useContext(ListContext);
    const { Notes } = currentContact;
    const breakpoints = useNwBreakPoints();

    const viewModelNotes = useUserPermission(
        'ViewModelNotes',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewClientNotes = useUserPermission(
        'ViewClientNotes',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewAgencyNotes = useUserPermission(
        'ViewAgencyNotes',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewServiceNotes = useUserPermission(
        'ViewServiceNotes',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );

    const viewNotes = useMemo(() => {
        if (currentContact) {
            if (currentContact.SubType === 1) return viewClientNotes;
            if (currentContact.SubType === 2) return viewAgencyNotes;
            if (currentContact.SubType === 3) return viewServiceNotes;
            return viewModelNotes;
        }
        return false;
    }, [currentContact, viewClientNotes, viewAgencyNotes, viewServiceNotes, viewModelNotes]);

    useEffect(() => {
        setCreateMode(createNotes);
        if (createNotes) {
            setItemToModify(null);
            setEditingMode(false);
        }
    }, [createNotes]);

    const notes = useMemo(() => {
        let notes = [...currentContact.Notes];
        notes.sort((note1, note2) => (getMoment(note1.DateCreated) < getMoment(note2.DateCreated) ? 1 : -1));
        notes = notes.filter((note) => {
            if (note.Visibility) {
                if (
                    note.Visibility.Areas.length === 0 ||
                    note.Visibility.Areas.find((area) => area === areaContext.area)
                ) {
                    return true;
                }
                return false;
            }
            return true;
        });
        if (type) {
            notes = notes.filter((note) => note.NoteTypeCode.toLowerCase() === type.toLowerCase());
        }
        return notes;
    }, [activePage, itemsPerPage, currentContact, type]);

    const notestorender = isInCardTab && !noPagination ? sliceData(activePage, itemsPerPage, notes) : notes;

    const handleCreated = () => {
        setItemToModify(null);
        setCreateMode(false);
        setEditingMode(false);
        afterNotesCreation();
    };

    const handlePageChange = (page) => {
        setActivePage(page);
    };

    const pagination = noPagination
        ? false
        : {
              total: notes.length,
              current: activePage,
              pageSize: itemsPerPage,
              onChange: handlePageChange,
          };

    useEffect(() => {
        checkValidPage(activePage, itemsPerPage, Notes, handlePageChange);
    });

    const deleteNote = (noteId) => {
        Axios.delete(`/notes/${noteId}`)
            .then(() => {
                forceReloadContact(currentContact.ID);
            })
            .catch((error) => {
                console.error('errore', error);
            });
    };

    const renderActions = (note) => {
        return [
            <NwDropDownMenu
                size="small"
                actions={[
                    {
                        name: '',
                        key: 'edit',
                        pinned: true,
                        icon: byPrefixAndName.fasl['pen'],
                        action: () => {
                            setCreateMode(false);
                            setItemToModify(note);
                            setEditingMode(true);
                        },
                    },
                    {
                        name: 'delete',
                        type: 'delete',
                        key: 'delete',
                        icon: byPrefixAndName.fasl['trash-alt'],
                        confirmationPlacement: 'left',
                        action: () => deleteNote(note.ID),
                    },
                ]}
            />,
        ];
    };

    const renderNoteList = (notes) => {
        return (
            <Wrapper>
                <List
                    size="small"
                    itemLayout="horizontal"
                    dataSource={notes}
                    renderItem={(note) => {
                        const { Text, NoteTypeCode, Visibility } = note;
                        return (
                            <List.Item actions={!readOnly && renderActions(note)}>
                                <List.Item.Meta
                                    description={
                                        <div className="note-description-container">
                                            <div className="tag-container">
                                                <Tag className="tag">
                                                    {
                                                        cachedList.noteTypes.items.find(
                                                            (el) => el.Code === NoteTypeCode
                                                        ).Name
                                                    }
                                                </Tag>
                                            </div>
                                            <div>
                                                {noEllipsis ? (
                                                    <p className="note-text">{Text}</p>
                                                ) : (
                                                    <NwEllipsis
                                                        alwaysModal
                                                        popover
                                                        popovertitle={'Note'}
                                                        rows={2}
                                                        text={Text}
                                                    />
                                                )}
                                                <CreatedBy object={note} />
                                                {Visibility.Areas.length > 0 && (
                                                    <Visibilities visibilities={Visibility.Areas} />
                                                )}
                                            </div>
                                        </div>
                                    }
                                />
                            </List.Item>
                        );
                    }}
                />
            </Wrapper>
        );
    };

    const renderCardContent = () => {
        return (
            <NwCardTabsContent>
                {viewNotes ? (
                    <>
                        <NwCardContent pagination={breakpoints !== 1 && isInCardTab ? pagination : null}>
                            {notestorender && notestorender.length > 0 ? (
                                renderNoteList(notestorender)
                            ) : (
                                <EmptyState message="no notes" />
                            )}
                        </NwCardContent>

                        {(editingMode || createMode) && (
                            <NoteAddEditDrawer
                                note={itemToModify}
                                contactID={currentContact.ID}
                                afterNotesCreation={handleCreated}
                            />
                        )}
                    </>
                ) : (
                    <LockContainer justifyContent="center" alignItems="center" $drawerMode={drawerMode}>
                        <NwIcon icon={byPrefixAndName.fasl['lock-alt']} />
                    </LockContainer>
                )}
            </NwCardTabsContent>
        );
    };

    return (
        // <DetailsCardContent viewNotes={viewNotes}>
        //{
        isInCardTab ? (
            renderCardContent()
        ) : (
            <NwCard
                title="Notes"
                fullheight
                icon={byPrefixAndName.fasl['sticky-note']}
                extra={
                    !readOnly && (
                        <NwAddButton
                            ghost
                            disabled={!viewModelNotes}
                            size="small"
                            onClick={() => {
                                setItemToModify(null);
                                setEditingMode(false);
                                setCreateMode(true);
                            }}
                            label="add new"
                        />
                    )
                }
            >
                {renderCardContent()}
            </NwCard>
        )
        //}
        // </DetailsCardContent>
    );
};

export default ContactDetailsNotes;
